import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Crown, FileText, Volume2, Brain, Mic, Zap, Loader2, Trash2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useUserStore } from '../../stores/userStore';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { usePublicPricing, formatPlanPrice } from '../../hooks/use-public-pricing';
import { useI18n } from '../../i18n';
import { BillingPricingStatus } from './billing-pricing-status';
import { billingErrorKey } from '../../billing/billing-errors';

export type LimitType = 'pdf_count' | 'pdf_size' | 'tts' | 'book_audio' | 'explain' | 'ask_ai' | 'realtime';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: LimitType;
  /** Optional: for pdf_size, the attempted file size in MB */
  attemptedSize?: number;
  /** Optional: callback for secondary action like "Delete a document" */
  onSecondaryAction?: () => void;
}

/** Icons only — titles, descriptions and pro-feature lines live in the locale files. */
const limitIcons: Record<LimitType, React.ElementType> = {
  pdf_count: FileText,
  pdf_size: FileText,
  tts: Volume2,
  book_audio: Volume2,
  explain: Brain,
  ask_ai: Brain,
  realtime: Mic,
};

export function UpgradeModal({ isOpen, onClose, limitType, attemptedSize, onSecondaryAction }: UpgradeModalProps) {
  const { t, tList, lang } = useI18n();
  const { getToken } = useAuth();
  const { limits, profile, createCheckoutSession } = useUserStore();
  const { pricing, loading: pricingLoading, error: pricingError, retry: retryPricing } = usePublicPricing();
  const isPaid = ['pro', 'paid', 'premium'].includes(profile?.subscription_tier || limits?.tier || '');
  const [isLoading, setIsLoading] = useState(false);
  const titleId = React.useId();
  const Icon = limitIcons[limitType];

  const monthlyLine = useMemo(
    () =>
      t('upgrade.priceMonthly', {
        price: pricing
          ? formatPlanPrice(pricing.monthlyAmount, pricing.currency)
          : '—',
      }),
    [pricing, t]
  );
  const annualLine = useMemo(
    () =>
      t('upgrade.priceAnnual', {
        price: pricing
          ? formatPlanPrice(pricing.annualAmount, pricing.currency)
          : '—',
      }),
    [pricing, t]
  );

  const handleUpgrade = async (planType: 'monthly' | 'annual') => {
    if (!pricing || pricingLoading || isLoading || isPaid) return;
    const token = await getToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const url = await createCheckoutSession(planType, token, lang);
      window.location.href = url;
    } catch (error) {
      toast.error(t(billingErrorKey(error, 'upgrade.checkoutFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  const getUsageDisplay = () => {
    if (!limits) return null;

    switch (limitType) {
      case 'pdf_count':
        return { current: limits.pdf.current ?? 0, max: limits.pdf.max ?? 2, label: t('upgrade.labels.documents') };
      case 'pdf_size':
        return { current: attemptedSize ?? 0, max: limits.pdfSize?.max ?? 10, label: t('upgrade.labels.fileSizeLimit') };
      case 'tts':
        return { current: limits.tts.current ?? 0, max: limits.tts.max ?? 500, label: t('upgrade.labels.wordsUsed') };
      case 'book_audio':
        return null;
      case 'explain':
        return { current: limits.explain.current ?? 0, max: limits.explain.max ?? 2, label: t('upgrade.labels.explanationsToday') };
      case 'ask_ai':
        return null;
      case 'realtime':
        return { current: limits.realtime?.current ?? 0, max: limits.realtime?.max ?? 60, label: t('upgrade.labels.secondsUsed') };
      default:
        return null;
    }
  };

  const usage = getUsageDisplay();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden overscroll-contain" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] p-6 text-white">
                <button
                  aria-label={t("common.close")}
                  onClick={onClose}
                  className="absolute top-4 end-4 p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 id={titleId} className="text-xl font-bold">{t(`upgrade.limits.${limitType}.title`)}</h2>
                  </div>
                </div>

                <p className="text-white/90 text-sm">{t(`upgrade.limits.${limitType}.description`)}</p>
              </div>

              {/* Usage indicator */}
              {usage && limitType !== 'pdf_size' && (
                <div className="px-6 pt-5">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">{t('upgrade.currentUsage')}</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {usage.current} / {usage.max} {usage.label}
                      </span>
                    </div>
                    <Progress value={(usage.current / (usage.max || 1)) * 100} className="h-2" />
                  </div>
                </div>
              )}

              {limitType === 'pdf_size' && attemptedSize && (
                <div className="px-6 pt-5">
                  <div className="bg-red-50 rounded-xl p-4">
                    <p className="text-sm text-red-700">
                      {t('upgrade.fileTooLargeBody', {
                        size: attemptedSize.toFixed(1),
                        max: limits?.pdfSize?.max ?? 10,
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Pro benefits */}
              <div className="px-6 pt-4 pb-2">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="h-4 w-4 text-[#7367FF]" />
                  <span className="text-sm font-semibold text-gray-900">{t('upgrade.upgradeToPro')}</span>
                </div>
                {!isPaid && <p className="mb-3 text-sm leading-relaxed text-gray-600">{t('upgrade.paidBenefitsPrompt')}</p>}
                <ul className="space-y-2">
                  {[t(`upgrade.limits.${limitType}.proFeature`), ...tList('upgrade.features')].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-[#3CFF9B] flex-shrink-0" />
                      <span className={i === 0 ? 'font-medium text-gray-900' : ''}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action buttons */}
              <div className="p-6 space-y-3">
                <BillingPricingStatus loading={pricingLoading} error={pricingError} retry={retryPricing} />
                {isPaid && <p role="status" className="text-sm text-slate-700">{t('billing.alreadySubscribed')} <a href="mailto:support@sourcity.ai" className="text-[#7367FF] underline">{t('billing.contactSupport')}</a></p>}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleUpgrade('monthly')}
                    disabled={isLoading || !pricing || pricingLoading || isPaid}
                    className="bg-[#7367FF] hover:bg-[#5f56e6] text-white"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : null}
                    <div className="text-center">
                      <div className="text-sm font-semibold">{t('upgrade.monthly')}</div>
                      <div className="text-xs opacity-80">{monthlyLine}</div>
                    </div>
                  </Button>
                  <Button
                    onClick={() => handleUpgrade('annual')}
                    disabled={isLoading || !pricing || pricingLoading || isPaid}
                    className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : null}
                    <div className="text-center">
                      <div className="text-sm font-semibold">{t('upgrade.annual')}</div>
                      <div className="text-xs opacity-80">{annualLine}</div>
                    </div>
                  </Button>
                </div>

                {/* Secondary action */}
                {limitType === 'pdf_count' && onSecondaryAction && (
                  <Button
                    onClick={() => { onSecondaryAction(); onClose(); }}
                    variant="outline"
                    className="w-full text-gray-600 border-gray-200"
                  >
                    <Trash2 className="h-4 w-4 me-2" />
                    {t('upgrade.deleteToFreeSpace')}
                  </Button>
                )}

                <button
                  onClick={onClose}
                  className="w-full text-center text-sm text-gray-400 hover:text-gray-600 transition-colors pt-1"
                >
                  {t('upgrade.maybeLater')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Hook to check limits before performing actions.
 * Returns a function that checks the relevant limit and opens the modal if exceeded.
 */
export function useUpgradeCheck() {
  const { limits } = useUserStore();

  const isAtLimit = (type: LimitType, fileSize?: number): boolean => {
    if (!limits) return false;

    switch (type) {
      case 'pdf_count':
        return !limits.pdf.unlimited && (limits.pdf.current ?? 0) >= (limits.pdf.max ?? 2);
      case 'pdf_size':
        return fileSize !== undefined && fileSize > (limits.pdfSize?.max ?? 10);
      case 'tts':
        return !limits.tts.unlimited && (limits.tts.current ?? 0) >= (limits.tts.max ?? 500);
      case 'book_audio':
        return false;
      case 'explain':
        return !limits.explain.unlimited && (limits.explain.current ?? 0) >= (limits.explain.max ?? 2);
      case 'ask_ai':
        return !['paid', 'premium', 'pro'].includes(String(limits.tier || '').toLowerCase());
      case 'realtime':
        return limits.realtime ? !limits.realtime.unlimited && (limits.realtime.current ?? 0) >= (limits.realtime.max ?? 60) : false;
      default:
        return false;
    }
  };

  return { isAtLimit, limits };
}
